import { idbRequest, withOfflineTransaction } from './db';
import { sha256Canonical } from './crypto';
import { getDeviceId } from './device';
import { offlineActionDecision } from './authEnvelope';
import type { LocalAuditEvent, OfflineCommandPriority, OutboxCommand } from './types';
import { nextAuditSequence } from './audit';

export interface LocalCommandContext {
  tenant_id: string;
  user_id: string;
  resource_type: string;
  resource_id: string;
  operation: string;
  offline_action: string;
  payload: unknown;
  priority?: OfflineCommandPriority;
  depends_on_command_id?: string | null;
  expected_revision?: string | number | null;
  domain_record?: Record<string, unknown> | null;
  allow_online_required?: boolean;
}

export interface LocalCommitResult {
  command: OutboxCommand;
  audit: LocalAuditEvent;
  domain_record?: Record<string, unknown> | null;
}

const appVersion = () => (import.meta as any).env?.VITE_APP_VERSION || '1.5.0-web';

async function nextUserSequence(tx: IDBTransaction, userId: string): Promise<number> {
  const store = tx.objectStore('meta');
  const key = `local_sequence:${userId}`;
  const current: any = await idbRequest(store.get(key));
  const next = Number(current?.value || 0) + 1;
  await idbRequest(store.put({ key, value: next, updated_at: new Date().toISOString() }));
  return next;
}

/**
 * Atomic local-first command commit. This is the canonical write path for any
 * feature classified OFFLINE_FULL/OFFLINE_LIMITED after it has been migrated.
 * The UI must not report durable success until this promise resolves.
 */
export async function commitLocalCommand(context: LocalCommandContext): Promise<LocalCommitResult> {
  if (!context.allow_online_required) {
    const decision = await offlineActionDecision(context.user_id, context.offline_action);
    if (!decision.allowed) throw new Error(decision.reason);
  }

  const deviceId = getDeviceId();
  const now = new Date().toISOString();
  const commandId = crypto.randomUUID();
  const payloadWithConcurrency = context.expected_revision === undefined
    ? context.payload
    : { ...(context.payload as any), expected_revision: context.expected_revision };
  const payloadHash = await sha256Canonical(payloadWithConcurrency);

  return withOfflineTransaction(['meta', 'domain_records', 'outbox_commands', 'audit_events'], 'readwrite', async tx => {
    const sequence = await nextUserSequence(
      tx,
      context.user_id,
    );

    const auditSequence = await nextAuditSequence(
      tx,
      context.tenant_id,
      context.user_id,
    );

    const command: OutboxCommand = {
      outbox_id: crypto.randomUUID(),
      command_id: commandId,
      tenant_id: context.tenant_id,
      user_id: context.user_id,
      device_id: deviceId,
      resource_type: context.resource_type,
      resource_id: context.resource_id,
      operation: context.operation,
      schema_version: 'offline-command-v1',
      app_version: appVersion(),
      payload: payloadWithConcurrency,
      payload_hash: payloadHash,
      created_at: now,
      sequence_no: sequence,
      status: context.depends_on_command_id ? 'PENDING' : 'READY',
      priority: context.priority || 'P2',
      idempotency_key: commandId,
      depends_on_command_id: context.depends_on_command_id || null,
      attempt_count: 0,
      last_attempt_at: null,
      next_attempt_at: null,
      server_ack_id: null,
      error_category: null,
      error_code: null,
    };
    await idbRequest(tx.objectStore('outbox_commands').add(command));

    let domainRecord: Record<string, unknown> | null = context.domain_record || null;
    if (domainRecord) {
      domainRecord = {
        ...domainRecord,
        local_key: (domainRecord as any).local_key || `${context.tenant_id}:${context.user_id}:${context.resource_type}:${context.resource_id}`,
        tenant_id: context.tenant_id,
        user_id: context.user_id,
        device_id: deviceId,
        resource_type: context.resource_type,
        resource_id: context.resource_id,
        sync_status: 'PENDING_SYNC',
        pending_command_id: commandId,
        updated_at: now,
      };
      await idbRequest(tx.objectStore('domain_records').put(domainRecord));
    }

    const audit: LocalAuditEvent = {
      audit_id: crypto.randomUUID(),
      tenant_id: context.tenant_id,
      device_id: deviceId,
      user_id: context.user_id,
      action: 'COMMAND_ENQUEUED',
      resource_type: context.resource_type,
      resource_id: context.resource_id,
      device_timestamp: now,
      sequence: auditSequence,
      app_version: appVersion(),
      metadata: { command_id: commandId, operation: context.operation, payload_hash: payloadHash, offline_action: context.offline_action },
      sync_status: 'PENDING_SYNC',
    };
    await idbRequest(tx.objectStore('audit_events').add(audit));
    return { command, audit, domain_record: domainRecord };
  });
}
